import React from 'react';
import { Sparkles, AlertTriangle, Crown } from 'lucide-react';

interface AIQuestionCounterProps {
  used: number;
  total: number;
  isPro?: boolean;
  compact?: boolean;
}

export default function AIQuestionCounter({ used, total, isPro = false, compact = false }: AIQuestionCounterProps) {
  const remaining = total - used;
  const pct = (used / total) * 100;
  const isLow = remaining <= 1 && remaining > 0;
  const isExhausted = remaining <= 0;

  if (compact) {
    if (isPro) {
      return (
        <div className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border border-accent/30 bg-accent/10 text-accent">
          <Crown size={12} />
          Unlimited
        </div>
      );
    }
    return null;
  }

  if (isPro) {
    return (
      <div className="rounded-xl p-3 border border-accent/30 bg-accent/5 flex items-center gap-2">
        <Crown size={14} className="text-accent" />
        <span className="text-xs font-semibold text-accent">Unlimited AI questions</span>
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-3 border ${
      isExhausted
        ? 'border-red-500/30 bg-red-500/5'
        : isLow
        ? 'border-amber-500/30 bg-amber-500/5' :'border-border bg-muted/50'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {isExhausted ? (
            <AlertTriangle size={14} className="text-red-400" />
          ) : (
            <Sparkles size={14} className="text-accent" />
          )}
          <span className="text-xs font-semibold text-foreground">AI Questions</span>
        </div>
        <span className={`text-xs font-bold font-tabular ${
          isExhausted ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-accent'
        }`}>
          {used} of {total} AI questions used
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isExhausted ? 'bg-red-500' : isLow ? 'bg-amber-500' : 'bg-accent'
          }`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <p className={`text-[10px] mt-1.5 ${isExhausted ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-muted-foreground'}`}>
        {isExhausted
          ? `You have used all ${total} free AI questions. Upgrade to Pro for unlimited access.`
          : isLow
          ? `Last free AI question`
          : `${remaining} question${remaining !== 1 ? 's' : ''} remaining`}
      </p>
    </div>
  );
}